import { overrideTailwindClasses } from 'tailwind-override';

import ArrowRightIcon from '@components/ArrowRightIcon';
import { useTranslation } from '@hooks/useTranslation';
import {
  footer,
  beta,
  common,
  hero,
  rule,
  comparison,
  oneBillionQuestion,
} from '@translations/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function IndexPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="w-full">
      <header className="flex justify-between p-4 lg:p-8 items-center">
        <h2 className="lg:text-xl">Climate Hour</h2>
        <Button
          caption={t(common.joinBeta)}
          onClick={() =>
            document
              .getElementById('view-cta')
              ?.scrollIntoView({ behavior: 'smooth' })
          }
        />
      </header>

      {/* Hero */}
      <div className="lg:p-8">
        <div className="lg:flex">
          <div style={{ flex: '50%' }}>
            <h1 className="p-4 my-6 text-4xl sm:text-5xl lg:text-6xl lg:pr-16">
              {t(hero.title)}
            </h1>
            <div className="mt-8 hidden lg:block space-x-4">
              <Button
                caption={t(hero.findOutMore)}
                onClick={() =>
                  document
                    .getElementById('view-3-5')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
            </div>
          </div>
          <div className="" style={{ flex: '50%' }}>
            <div className="relative mx-auto w-64 sm:w-96 min-h-128 sm:min-h-144 lg:min-h-192">
              <PersonalActionTile
                className="top-2 left-28 sm:left-56"
                // style={{ left: '30%' }}
                tKey={hero.energy}
                background="energy"
              />
              <PersonalActionTile
                className="top-24 left-0"
                tKey={hero.car}
                a={'Stoping'}
                b={'driving'}
                background="car"
              />
              <PersonalActionTile
                className="top-48 left-36 sm:left-72 xl:left-72"
                tKey={hero.food}
                a={'Going'}
                b={'vegan'}
                background="food"
              />
              <PersonalActionTile
                className="top-52 left-2 md:-left-16"
                tKey={hero.fly}
                a={'Flying'}
                b={'less'}
                background="fly"
              />
              <PersonalActionTile
                className="top-80 left-20"
                tKey={hero.civicEngagement}
                a={'Civic'}
                b={'engagement'}
                background="civic-engagement"
              />
            </div>
          </div>

          <div className="mb-8 lg:hidden block space-y-4 mx-auto text-center p-8 max-w-md">
            <Button
              className="w-full block"
              caption="Find out more"
              onClick={() =>
                document
                  .getElementById('view-3-5')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            />
          </div>
        </div>
      </div>

      {/* 3.5% */}
      <div
        id="view-3-5"
        className="p-4 py-24 lg:p-8 lg:py-48 bg-white flex justify-center items-center"
      >
        <div className="mx-auto text-black max-w-lg text-center">
          <h1 className=" text-black text-4xl text-center max-w-lg">
            {t(rule.headline)}
          </h1>
          {/* <Button caption="Tell me more" className="mt-8 border-black text-3xl w-full sm:w-auto" onClick={() => document.getElementById('comparison')?.scrollIntoView({ behavior: 'smooth' })} /> */}
        </div>
      </div>

      {/* personal vs. political action */}
      <div id="comparison" className="bg-black sm:grid sm:grid-flow-col py-16">
        <div>
          <div className="mx-auto max-w-4xl p-8 sm:p-16">
            <h2 className="text-4xl font-bold mb-4">
              -10%
              <br />
              <span className="text-lg block ">{t(comparison.emissions)}</span>
            </h2>
            {/* <p className="mb-8">
              <img src="/assets/humans-1b.svg" className="h-8 mb-2 inline-block" style={{ marginRight: '0.6rem' }} />
              <img src="/assets/humans-empty.svg" className="h-8 mb-2 inline-block" />
            </p> */}
            <p style={{ fontFamily: 'Nimbus Sans' }}>
              {t(comparison.personal)}
            </p>
          </div>
        </div>
        <div>
          <div className="mx-auto max-w-4xl p-8 sm:p-16">
            <h2 className="text-4xl font-bold mb-4">
              <img
                src="/assets/party.svg"
                className="h-12 inline-block align-bottom  mr-2"
              />
              Net Zero
              <br />
              <span className="text-lg block ">{t(comparison.emissions)}</span>
            </h2>
            {/* <p className="mb-8">
              <img src="/assets/humans-3.5.svg" className="h-8 mb-2 inline-block" style={{ marginRight: '0.6rem' }} />
              <img src="/assets/humans-empty.svg" className="h-8 mb-2 inline-block" />
            </p> */}
            <p style={{ fontFamily: 'Nimbus Sans' }}>
              {t(comparison.political)}
            </p>
          </div>
        </div>
        {/* <h1 className="mx-auto text-white text-4xl text-center max-w-lg">More is more.</h1> */}
      </div>
      <Separator />

      {/* 100M people question */}
      <div className="py-16 bg-black">
        <h1 className="mx-auto text-white p-4 sm:p-8 text-4xl text-center max-w-xl lg:max-w-4xl leading-normal">
          {t(oneBillionQuestion.headline)}
        </h1>
        <div className="block sm:flex mx-auto justify-center">
          <div>
            <CivicActionTile
              picture="lobbying"
              captionKey={oneBillionQuestion.lobbying}
            />
            <CivicActionTile
              picture="brands"
              captionKey={oneBillionQuestion.brands}
            />
            <CivicActionTile
              picture="earth"
              captionKey={oneBillionQuestion.earth}
            />
          </div>
          <div className="sm:mt-8">
            <CivicActionTile
              picture="companies"
              captionKey={oneBillionQuestion.companies}
            />
            <CivicActionTile
              picture="learn"
              captionKey={oneBillionQuestion.learn}
            />
            <CivicActionTile
              picture="more"
              captionKey={oneBillionQuestion.more}
            />
          </div>
        </div>
        <div className="mx-auto flex flex-wrap justify-center items-start"></div>
      </div>
      <Separator />

      {/* CTA */}
      <div id="view-cta" className="py-16">
        <div className="mx-auto">
          <h1
            className="text-6xl text-center p-4"
            style={{ fontFamily: 'Nimbus Sans' }}
          >
            {t(beta.whoIfNotYou)}
            <br />
            {t(beta.whenIfNotNow)}
          </h1>
          <p className="text-lg text-center p-4">{t(beta.description)}</p>
        </div>
        <div>
          <div className="block sm:flex mx-auto justify-center p-8 flex-wrap">
            <Testimonial
              statement={t(beta.testimonialA)}
              author="Pauline Weber"
              picture="testimonial-b"
            />
            <Testimonial
              statement={t(beta.testimonialB)}
              author="Franz Neumann"
              picture="testimonial-a"
            />
            <div style={{ flexBasis: '100%', height: '0' }}></div>
            <div
              className="bg-white max-w-xl border-4 border-white lg:max-w-xl mx-auto flex flex-col justify-between"
              style={{
                minHeight: '16rem',
                boxSizing: 'content-box',
              }}
            >
              <p className="text-black p-10 text-xl">{t(beta.ctaBoxText)}</p>
              <p className="text-xl text-black text-right mr-2 mb-2">
                <a href="https://climatehour.de">
                  {t(beta.ctaBoxLink)}
                  <ArrowRightIcon className="h-12 mb-1 inline-block" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div
        className="flex justify-between flex-wrap p-8"
        style={{ fontFamily: 'Nimbus Sans', borderTop: '1px solid white' }}
      >
        <p>
          &copy;{' '}
          <a href="https://imagine-zero.org" target="_blank">
            Imagine Zero e.V.
          </a>
        </p>
        <p>
          <a href="https://imagine-zero.org/imprint" target="_blank">
            {t(footer.imprint)}
          </a>
          <LanguageSwitcher />
        </p>
        <p className="w-full text-center mt-8 mb-4">{t(footer.madeWith)}</p>
      </div>
    </div>
  );
}

export function Button({ caption, className, onClick }: any): JSX.Element {
  return (
    <button
      className={overrideTailwindClasses(
        `border-white border px-3 py-1 lg:text-xl lg:px-4 lg:py-2 ${className}`
      )}
      onClick={onClick}
    >
      {caption}
    </button>
  );
}

export function PersonalActionTile({
  className,
  tKey,
  background,
  style,
}: any): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={`absolute w-32 h-48 sm:w-48 sm:h-64 xl:w-64 xl:h-96 bg-cover bg-center ${className}`}
      style={{
        ...(style || {}),
        backgroundImage: `url(/assets/${background}.jpg)`,
      }}
    >
      <div className="relative">
        <h3 className="text-sm absolute top-8 right-0 text-right sm:text-lg xl:text-2xl">
          <span
            className="inline-block bg-black px-2"
            style={{ fontFamily: 'Nimbus Sans' }}
          >
            {t(tKey.a)}
          </span>
          <br />
          <span className="inline-block bg-black font-bold px-2">
            {t(tKey.b)}
          </span>
        </h3>
      </div>
    </div>
  );
}

export function CivicActionTile({
  className,
  captionKey,
  picture,
  style,
}: any): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={`w-72 lg:w-96 h-48 lg:h-56 m-2 mx-auto sm:mx-1 my-2 lg:mx-2 lg:my-4 bg-cover bg-center ${className} flex items-end justify-end`}
      style={{
        minWidth: '16rem',
        backgroundColor: 'pink',
        backgroundImage: `url(/assets/civic-actions/${picture}.jpg)`,
        ...(style || {}),
      }}
    >
      <h3
        className="text-sm lg:text-base bg-white text-black w-9/12 p-4"
        style={{ fontFamily: 'Nimbus Sans' }}
      >
        {t(captionKey)}
      </h3>
    </div>
  );
}
function Separator(): JSX.Element {
  return <div className="mx-8" style={{ borderBottom: '1px solid white' }} />;
}
function Testimonial({ statement, author, picture }: any): JSX.Element {
  return (
    <div
      style={{ border: '1px solid white' }}
      className={`w-full sm:w-72 lg:w-96 h-48 lg:h-48 m-2 mx-auto sm:mx-1 my-2 lg:mx-2 lg:my-4 bg-cover bg-center flex`}
    >
      <div
        className="bg-blue-700 w-36 sm:w-32 flex-shrink-0 bg-cover bg-center"
        style={{
          maxWidth: '40%',
          backgroundImage: `url(/assets/${picture}.jpg)`,
        }}
      ></div>
      <div className="p-4 flex flex-col justify-between">
        <h1 className="lg:text-lg">“{statement}”</h1>
        <p className="text-sm" style={{ fontFamily: 'Nimbus Sans' }}>
          {author}
        </p>
      </div>
    </div>
  );
}
